import { GAME_CONFIG, GAME_STATES, DIRECTIONS } from './constants.js';
import { Snake } from './snake.js';
import { Food } from './food.js';
import { PowerUpManager } from './powerups.js';
import { Renderer } from './renderer.js';
import { InputManager } from './input.js';
import { StorageManager } from './storage.js';

export class Game {
    constructor() {
        this.canvas = document.getElementById('game-canvas');
        this.ctx = this.canvas.getContext('2d');
        
        // Initialize game components
        this.snake = new Snake();
        this.food = new Food();
        this.powerUpManager = new PowerUpManager();
        this.renderer = new Renderer(this.ctx, this.canvas);
        this.inputManager = new InputManager(this);
        this.storageManager = new StorageManager();
        
        // Game state
        this.state = GAME_STATES.MENU;
        this.score = 0;
        this.highScore = 0; // Will be loaded asynchronously
        this.level = 1;
        this.gameSpeed = GAME_CONFIG.INITIAL_SPEED;
        this.lastUpdateTime = 0;
        this.animationId = null;
        
        // Grid calculations
        this.gridWidth = Math.floor(GAME_CONFIG.CANVAS_WIDTH / GAME_CONFIG.GRID_SIZE);
        this.gridHeight = Math.floor(GAME_CONFIG.CANVAS_HEIGHT / GAME_CONFIG.GRID_SIZE);
        
        // Initialize canvas
        this.setupCanvas();
        this.setupEventListeners();
        this.initializeGame();
    }
    
    async initializeGame() {
        // Load high score asynchronously
        try {
            this.highScore = await this.storageManager.getHighScore();
            console.log(`🏆 Loaded high score: ${this.highScore}`);
        } catch (error) {
            console.error('Failed to load high score:', error);
            this.highScore = 0;
        }
        this.updateUI();
    }
    
    setupCanvas() {
        // Set canvas size
        this.canvas.width = GAME_CONFIG.CANVAS_WIDTH;
        this.canvas.height = GAME_CONFIG.CANVAS_HEIGHT;
        
        // Make canvas responsive
        this.updateCanvasSize();
        window.addEventListener('resize', () => this.updateCanvasSize());
    }
    
    updateCanvasSize() {
        const container = this.canvas.parentElement;
        const containerWidth = container.clientWidth - 40; // padding
        const containerHeight = window.innerHeight * 0.7; // 70% of screen height
        
        const scaleX = containerWidth / GAME_CONFIG.CANVAS_WIDTH;
        const scaleY = containerHeight / GAME_CONFIG.CANVAS_HEIGHT;
        const scale = Math.min(scaleX, scaleY, 1);
        
        this.canvas.style.width = `${GAME_CONFIG.CANVAS_WIDTH * scale}px`;
        this.canvas.style.height = `${GAME_CONFIG.CANVAS_HEIGHT * scale}px`;
    }
    
    setupEventListeners() {
        // UI Event Listeners
        document.getElementById('start-btn').addEventListener('click', () => this.startGame());
        document.getElementById('pause-btn').addEventListener('click', () => this.togglePause());
        document.getElementById('resume-btn').addEventListener('click', () => this.resumeGame());
        document.getElementById('restart-btn').addEventListener('click', () => this.restartGame());
        document.getElementById('play-again-btn').addEventListener('click', () => this.startGame());
        document.getElementById('main-menu-btn').addEventListener('click', () => this.showMainMenu());
        document.getElementById('main-menu-btn-2').addEventListener('click', () => this.showMainMenu());
        document.getElementById('leaderboard-btn').addEventListener('click', () => this.showLeaderboard());
        document.getElementById('leaderboard-btn-2').addEventListener('click', () => this.showLeaderboard());
        document.getElementById('instructions-btn').addEventListener('click', () => this.showInstructions());
        document.getElementById('save-score-btn').addEventListener('click', () => this.saveScore());
        
        // Modal close buttons
        document.getElementById('close-leaderboard').addEventListener('click', () => this.hideLeaderboard());
        document.getElementById('close-instructions').addEventListener('click', () => this.hideInstructions());
        
        // Close modals when clicking outside
        document.getElementById('leaderboard-modal').addEventListener('click', (e) => {
            if (e.target.id === 'leaderboard-modal') this.hideLeaderboard();
        });
        document.getElementById('instructions-modal').addEventListener('click', (e) => {
            if (e.target.id === 'instructions-modal') this.hideInstructions();
        });
        
        // Mobile control buttons
        document.querySelectorAll('.control-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const direction = btn.dataset.direction;
                this.handleInput(direction);
            });
        });
        
        // Prevent context menu on long press
        this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    }
    
    startGame() {
        this.state = GAME_STATES.PLAYING;
        this.score = 0;
        this.level = 1;
        this.gameSpeed = GAME_CONFIG.INITIAL_SPEED;
        
        // Reset game objects
        this.snake.reset();
        this.food.spawn(this.gridWidth, this.gridHeight, this.snake.getSegments());
        this.powerUpManager.reset();
        
        // Update UI
        this.showScreen('game-screen');
        this.updateUI();
        
        // Start game loop
        this.lastUpdateTime = performance.now();
        this.gameLoop();
    }
    
    togglePause() {
        if (this.state === GAME_STATES.PLAYING) {
            this.pauseGame();
        } else if (this.state === GAME_STATES.PAUSED) {
            this.resumeGame();
        }
    }
    
    pauseGame() {
        this.state = GAME_STATES.PAUSED;
        this.showScreen('pause-screen');
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }
    
    resumeGame() {
        this.state = GAME_STATES.PLAYING;
        this.showScreen('game-screen');
        this.lastUpdateTime = performance.now();
        this.gameLoop();
    }
    
    restartGame() {
        this.startGame();
    }
    
    showMainMenu() {
        this.state = GAME_STATES.MENU;
        this.showScreen('start-screen');
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }
    
    gameOver() {
        this.state = GAME_STATES.GAME_OVER;
        this.showScreen('game-over-screen');
        
        // Check for new high score
        const isNewHighScore = this.score > this.highScore;
        if (isNewHighScore) {
            this.highScore = this.score;
            this.storageManager.setHighScore(this.highScore);
            document.getElementById('new-high-score').classList.remove('hidden');
        } else {
            document.getElementById('new-high-score').classList.add('hidden');
        }
        
        // Always show name input section to save score (reset for each game)
        document.getElementById('name-input-section').classList.remove('hidden');
        document.getElementById('player-name').value = ''; // Clear previous name
        document.getElementById('player-email').value = '';
        document.getElementById('email-consent').checked = false;
        document.getElementById('save-score-btn').textContent = 'Save Score';
        document.getElementById('save-score-btn').disabled = false;
        document.getElementById('save-score-btn').style.background = '';
        
        document.getElementById('final-score').textContent = this.score;
        this.updateUI();
        
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }
    
    gameLoop() {
        if (this.state !== GAME_STATES.PLAYING) return;
        
        const currentTime = performance.now();
        const deltaTime = currentTime - this.lastUpdateTime;
        
        if (deltaTime >= this.gameSpeed) {
            this.update(deltaTime);
            this.lastUpdateTime = currentTime;
        }
        
        this.render();
        this.animationId = requestAnimationFrame(() => this.gameLoop());
    }
    
    update(deltaTime) {
        // Update snake
        this.snake.update();
        
        // Check collisions
        if (this.checkCollisions()) {
            this.gameOver();
            return;
        }
        
        // Check food collection
        if (this.checkFoodCollection()) {
            this.score += this.food.getScore() * this.powerUpManager.getCurrentMultiplier();
            this.snake.grow();
            this.food.spawn(this.gridWidth, this.gridHeight, this.snake.getSegments());
            this.updateLevel();
            this.updateUI();
        }
        
        // Check power-up collection
        const collectedPowerUp = this.powerUpManager.checkCollection(this.snake.getHead());
        if (collectedPowerUp) {
            this.score += collectedPowerUp.score * this.powerUpManager.getCurrentMultiplier();
            this.powerUpManager.activatePowerUp(collectedPowerUp);
            this.updateUI();
        }
        
        // Update power-ups
        this.powerUpManager.update(deltaTime);
        
        // Spawn power-ups occasionally
        if (Math.random() < GAME_CONFIG.POWERUP_SPAWN_CHANCE && !this.powerUpManager.hasActivePowerUp()) {
            this.powerUpManager.spawnPowerUp(this.gridWidth, this.gridHeight, this.snake.getSegments(), this.food.getPosition());
        }
    }
    
    checkCollisions() {
        const head = this.snake.getHead();
        
        // Wall collision
        if (head.x < 0 || head.x >= this.gridWidth || head.y < 0 || head.y >= this.gridHeight) {
            return true;
        }
        
        // Self collision
        return this.snake.checkSelfCollision();
    }
    
    checkFoodCollection() {
        const head = this.snake.getHead();
        const foodPos = this.food.getPosition();
        return head.x === foodPos.x && head.y === foodPos.y;
    }
    
    updateLevel() {
        const newLevel = Math.floor(this.score / GAME_CONFIG.LEVEL_UP_SCORE) + 1;
        if (newLevel > this.level) {
            this.level = newLevel;
            this.gameSpeed = Math.max(
                GAME_CONFIG.INITIAL_SPEED - (this.level * GAME_CONFIG.SPEED_INCREMENT),
                GAME_CONFIG.MIN_SPEED
            );
        }
    }
    
    render() {
        this.renderer.clear();
        this.renderer.drawBackground();
        this.renderer.drawPitchMarkings();
        this.food.render(this.ctx, GAME_CONFIG.GRID_SIZE);
        this.powerUpManager.render(this.ctx, GAME_CONFIG.GRID_SIZE);
        
        // Render snake with current power-up effects
        const snakeColor = this.powerUpManager.getSnakeColor();
        this.snake.render(this.ctx, GAME_CONFIG.GRID_SIZE, snakeColor);
        
        // Render power-up effects
        this.renderer.drawPowerUpEffects(this.powerUpManager.getActiveEffects());
    }
    
    handleInput(direction) {
        if (this.state !== GAME_STATES.PLAYING) return;
        
        const directionMap = {
            'up': DIRECTIONS.UP,
            'down': DIRECTIONS.DOWN,
            'left': DIRECTIONS.LEFT,
            'right': DIRECTIONS.RIGHT
        };
        
        const newDirection = directionMap[direction];
        if (newDirection) {
            this.snake.changeDirection(newDirection);
        }
    }
    
    showScreen(screenId) {
        // Hide all screens
        document.querySelectorAll('.screen').forEach(screen => {
            screen.classList.remove('active');
        });
        
        // Show target screen
        document.getElementById(screenId).classList.add('active');
    }
    
    updateUI() {
        document.getElementById('current-score').textContent = this.score;
        document.getElementById('high-score').textContent = this.highScore;
        
        const multiplier = this.powerUpManager.getCurrentMultiplier();
        const multiplierDisplay = document.getElementById('multiplier-display');
        multiplierDisplay.textContent = `${multiplier}x`;
        
        if (multiplier > 1) {
            multiplierDisplay.style.background = 'rgba(255, 193, 7, 0.3)';
            multiplierDisplay.style.borderColor = '#FFC107';
        } else {
            multiplierDisplay.style.background = 'rgba(255, 193, 7, 0.2)';
            multiplierDisplay.style.borderColor = '#FFC107';
        }
    }
    
    async showLeaderboard() {
        const listElement = document.getElementById('leaderboard-list');
        
        // Show loading state
        listElement.innerHTML = '<p style="text-align: center; color: #81C784;">Loading leaderboard...</p>';
        document.getElementById('leaderboard-modal').classList.add('active');
        
        try {
            const leaderboard = await this.storageManager.getLeaderboard(50);
            
            listElement.innerHTML = '';
            
            if (leaderboard.length === 0) {
                listElement.innerHTML = '<p style="text-align: center; color: #81C784;">No scores yet. Be the first!</p>';
                return;
            }
            
            leaderboard.forEach((entry, index) => {
                const item = document.createElement('div');
                item.className = `leaderboard-item rank-${index + 1}`;
                
                const rank = document.createElement('span');
                rank.className = 'rank';
                rank.textContent = `#${index + 1}`;
                
                const name = document.createElement('span');
                name.className = 'player-name';
                name.textContent = entry.name;
                
                const score = document.createElement('span');
                score.className = 'score';
                score.textContent = entry.score.toLocaleString();
                
                const date = document.createElement('span');
                date.className = 'date';
                date.textContent = new Date(entry.date).toLocaleDateString();
                
                item.appendChild(rank);
                item.appendChild(name);
                item.appendChild(score);
                item.appendChild(date);
                listElement.appendChild(item);
            });
            
            // Add indicator if using local data
            if (!navigator.onLine || !await this.storageManager.apiService.checkServerHealth()) {
                const offlineIndicator = document.createElement('p');
                offlineIndicator.style.cssText = 'text-align: center; color: #FFC107; font-size: 0.9rem; margin-top: 1rem;';
                offlineIndicator.textContent = '📴 Showing offline data';
                listElement.appendChild(offlineIndicator);
            }
            
        } catch (error) {
            console.error('Failed to load leaderboard:', error);
            listElement.innerHTML = '<p style="text-align: center; color: #F44336;">Failed to load leaderboard. Please try again.</p>';
        }
    }
    
    hideLeaderboard() {
        document.getElementById('leaderboard-modal').classList.remove('active');
    }
    
    showInstructions() {
        document.getElementById('instructions-modal').classList.add('active');
    }
    
    hideInstructions() {
        document.getElementById('instructions-modal').classList.remove('active');
    }
    
    async saveScore() {
        const playerName = document.getElementById('player-name').value.trim() || 'Anonymous';
        const playerEmail = document.getElementById('player-email').value.trim();
        const emailConsent = document.getElementById('email-consent').checked;
        const contactDetails = playerEmail && emailConsent
            ? { email: playerEmail, emailConsent: true }
            : {};
        const saveButton = document.getElementById('save-score-btn');
        const originalText = saveButton.textContent;
        
        // Show saving state
        saveButton.textContent = 'Saving...';
        saveButton.disabled = true;
        
        try {
            const result = await this.storageManager.addScore(playerName, this.score, new Date(), contactDetails);
            
            if (result.success) {
                // Show success message
                saveButton.textContent = result.global ? 'Saved to Global Leaderboard!' : 'Saved Locally';
                saveButton.style.background = '#4CAF50';
                
                setTimeout(async () => {
                    // Force refresh leaderboard from server after saving
                    if (result.global) {
                        // Clear any cached leaderboard to force fresh fetch
                        try {
                            await this.storageManager.apiService.getLeaderboard(50);
                        } catch (error) {
                            console.error('Failed to refresh leaderboard:', error);
                        }
                    }
                    // Show leaderboard but keep input section visible for next game
                    this.showLeaderboard();
                }, 1000);
            } else {
                throw new Error(result.error || 'Failed to save score');
            }
        } catch (error) {
            console.error('Failed to save score:', error);
            saveButton.textContent = 'Save Failed';
            saveButton.style.background = '#F44336';
            
            setTimeout(() => {
                saveButton.textContent = originalText;
                saveButton.style.background = '';
                saveButton.disabled = false;
            }, 2000);
        }
    }
    
    // Getter methods for other classes
    getGridDimensions() {
        return { width: this.gridWidth, height: this.gridHeight };
    }
    
    getGameState() {
        return this.state;
    }
    
    getScore() {
        return this.score;
    }
}
